import { memo, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

import {
    FACEBOOK_PIXEL_CONFIG,
    getPixelId,
    isTrackingEnabled,
    type AdvancedMatchingParams,
} from './config';
import { fbPageView } from './fbpixel';

/**
 * Sanitize phone number for Meta Pixel Advanced Matching
 * Removes all non-digit characters (+, spaces, dashes, etc.)
 * Meta Pixel expects digits only including country code and area code
 */
const sanitizePhoneNumber = (
    phone: string | null | undefined,
): string | undefined => {
    if (!phone) return undefined;

    // Keep only digits (remove +, spaces, dashes, parentheses, etc.)
    const digitsOnly = phone.replace(/\D/g, '');

    return digitsOnly.length > 0 ? digitsOnly : undefined;
};

interface MetaPixelProviderProps {
    children: React.ReactNode;
    userEmail?: string | null | undefined;
    userPhone?: string | null | undefined;
}

export const MetaPixelProvider = memo(
    ({ children, userEmail, userPhone }: MetaPixelProviderProps) => {
        const pixelId = getPixelId();
        const isTrackingEnabledValue = isTrackingEnabled();

        useEffect(() => {
            if (
                isTrackingEnabledValue &&
                pixelId &&
                typeof window !== 'undefined'
            ) {
                // Initialize Facebook Pixel after script loads
                const initPixel = () => {
                    if (window.fbq) {
                        // Prepare advanced matching params if user is logged in
                        const advancedMatchingParams: AdvancedMatchingParams =
                            {};

                        if (userEmail) {
                            advancedMatchingParams.em = userEmail;
                        }

                        const sanitizedPhone = sanitizePhoneNumber(userPhone);

                        if (sanitizedPhone) {
                            advancedMatchingParams.ph = sanitizedPhone;
                        }

                        // Initialize with advanced matching if user data is available
                        if (Object.keys(advancedMatchingParams).length > 0) {
                            window.fbq('init', pixelId, advancedMatchingParams);
                        } else {
                            window.fbq('init', pixelId);
                        }

                        window.fbq('track', 'PageView');
                    }
                };

                // Wait for script to load or initialize immediately if already loaded
                if (window.fbq) {
                    initPixel();
                } else {
                    // Listen for script load
                    const checkFbq = setInterval(() => {
                        if (window.fbq) {
                            clearInterval(checkFbq);
                            initPixel();
                        }
                    }, FACEBOOK_PIXEL_CONFIG.retryInterval);

                    // Clear interval after timeout to avoid memory leaks
                    setTimeout(
                        () => clearInterval(checkFbq),
                        FACEBOOK_PIXEL_CONFIG.initTimeout,
                    );
                }
            }
        }, [pixelId, isTrackingEnabledValue, userEmail, userPhone]);

        return (
            <>
                {isTrackingEnabledValue && pixelId && (
                    <Helmet>
                        <script>
                            {`
                                !function(f,b,e,v,n,t,s)
                                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                                n.queue=[];t=b.createElement(e);t.async=!0;
                                t.src=v;s=b.getElementsByTagName(e)[0];
                                s.parentNode.insertBefore(t,s)}(window, document,'script',
                                'https://connect.facebook.net/en_US/fbevents.js');
                            `}
                        </script>
                        <noscript>
                            {`<img alt="Facebook Pixel" height="1" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1" style="display:none" width="1" />`}
                        </noscript>
                    </Helmet>
                )}
                {children}
            </>
        );
    },
);

MetaPixelProvider.displayName = 'MetaPixelProvider';

// Enhanced tracker for all page changes
export const MetaPixelPageTracker = memo(() => {
    const location = useLocation();
    const lastTrackedPath = useRef<string>('');

    useEffect(() => {
        const currentPath = `${location.pathname}${location.search}`;

        // Avoid duplicate tracking if the same path is tracked multiple times quickly
        if (lastTrackedPath.current === currentPath) {
            return;
        }

        const params = {
            page_location: window.location.href,
            page_path: location.pathname,
            page_title: document.title,
        };

        // Track page view on route changes
        fbPageView(params);
        lastTrackedPath.current = currentPath;
    }, [location.pathname, location.search]);

    return null;
});

MetaPixelPageTracker.displayName = 'MetaPixelPageTracker';
